import type { AdminProfile, PatientProfile, ProviderProfile, TaskType, UUID } from "@/core/types";
import { isProviderProfile, isAdminProfile } from "@/core/types";
import { provider_pcp } from "@/config/roles";
import type { RootState } from "@/lib/types/state";

export const selectProfile = (
  state: RootState,
): AdminProfile | PatientProfile | ProviderProfile | undefined => {
  return state?.auth?.profile;
};

export const selectPatientProfile = (state: RootState): PatientProfile | undefined => {
  const profile = state?.auth?.profile;

  if (!profile || isProviderProfile(profile) || isAdminProfile(profile)) return undefined;

  return profile;
};

export const selectProviderProfile = (state: RootState): ProviderProfile | undefined => {
  const profile = state?.auth?.profile;

  if (!profile || !isProviderProfile(profile)) return undefined;

  return profile;
};

export const selectAdminProfile = (state: RootState): AdminProfile | undefined => {
  const profile = state?.auth?.profile;

  if (!profile || !isAdminProfile(profile)) return undefined;

  return profile;
};

export const selectPatientId = (state: RootState): undefined | UUID => {
  return state?.auth?.profile?.username;
};

export const selectIsProfileLoaded = (state: RootState): boolean => !!state?.auth?.profile;

export const selectProfileEmail = (state: RootState): string | undefined =>
  state?.auth?.profile?.email;

export const selectProfileUsername = (state: RootState): string | undefined =>
  state?.auth?.profile?.username;

export const selectRole = (state: RootState): null | string | undefined => {
  return state?.auth?.role;
};

export const selectIsUserPCP = (state: RootState): boolean => state?.auth?.role === provider_pcp;

export const selectDaysUntilPasswordReset = (state: RootState): null | number | undefined => {
  return state?.auth?.daysUntilPasswordReset;
};

export const selectIsPatientInactive = (state: RootState): boolean =>
  state?.auth?.profile?.status === "INACTIVE";

export const selectIsHCPPendingCancellationAcknowledgement = (state: RootState): boolean => {
  const profile = selectPatientProfile(state);
  const acknowledgements = profile?.acknowledgements as Record<string, unknown> | null | undefined;

  return !!(
    profile?.group_type === "hcp_program" && !("hcp_auto_cancellation" in (acknowledgements ?? {}))
  );
};

export const selectIsPatientWaitListed = (state: RootState): boolean =>
  selectPatientProfile(state)?.is_wait_listed === true;

export const selectShouldShowHCPDIModal = (state: RootState): boolean => {
  const profile = selectPatientProfile(state);
  const acknowledgements = profile?.acknowledgements as Record<string, unknown> | null | undefined;

  return !!(
    profile &&
    profile.group_type === "hcp_program" &&
    profile.schedule_video_call &&
    !(acknowledgements?.hcp_di_reminder ?? false) &&
    profile.status !== "INACTIVE"
  );
};

export const selectTaskIdByType = (state: RootState, taskType: TaskType): undefined | UUID => {
  const tasks = selectPatientProfile(state)?.tasks;
  const task = tasks?.find((task) => task.task_type === taskType);

  return task?.task_id;
};
